/** settings bridge 的底层 HTTP/文件传输与统一结果解析。 */
import type { EngineMeta, PromptConfigDraft } from '../prompt-tool-types.ts'
import type { HostDefaultModel, SkillCatalogEntry } from './prompt-tool-fields.ts'
import type { ModuleFacts } from '../../shared/engine-capabilities.ts'
import type { InstructionsSnapshot } from '../../shared/instructions.ts'
import {
  EDIT_TARGET_HEADER,
  MAX_BRIDGE_BODY_BYTES,
  SETTINGS_BRIDGE_PREFIX,
  type BridgeErrorPayload,
} from '../../shared/bridge-contract.ts'

interface BridgeSuccessExtras {
  providers?: string[]
  modelCatalog?: Record<string, string[]>
  activeSkillsDirs?: string[]
  skillCatalog?: SkillCatalogEntry[]
  skillsComplete?: boolean
  /** 用户添加的技能文件夹（只引用，不复制）。 */
  skillFolders?: string[]
  templatePreStepCount?: number
  presetParams?: Record<string, unknown>
  hostDefaultModel?: HostDefaultModel
  moduleFacts?: ModuleFacts
  meta?: { meta: EngineMeta }
  overrides?: { overrides: Record<string, unknown> }
  variables?: { variables: Record<string, string>; enabled: boolean }
  promptConfigs?: { promptConfigs: PromptConfigDraft[] }
  /** 指令文件快照（独立来源）：正文、身份、版本与读取状态来自同一次读取。 */
  instructions?: InstructionsSnapshot
}

export type { BridgeSettingsView } from '../../shared/bridge-contract.ts'

export type BridgeResult<T> = ({ ok: true; value: T } & BridgeSuccessExtras) | BridgeErrorPayload

export const errorMessage = (error: unknown): string => error instanceof Error ? error.message : String(error)

const isBridgeResultPayload = (payload: unknown): payload is BridgeResult<unknown> => {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return false
  const record = payload as Record<string, unknown>
  if (typeof record.ok !== 'boolean') return false
  return !record.ok || 'value' in record
}

/**
 * 宿主尚未就绪或路由未注册时，HTTP 层可能返回空体/非 JSON 响应；
 * 统一转成可诊断的桥接错误，不把原生 response.json() 异常透传给 UI。
 */
async function readBridgeResponse<T>(response: Response): Promise<BridgeResult<T>> {
  const text = await response.text()
  if (text.trim().length === 0) {
    return { ok: false, message: `桥接接口返回空响应（HTTP ${response.status}）` }
  }
  let payload: unknown
  try {
    payload = JSON.parse(text) as unknown
  } catch {
    const preview = text.trim().replace(/\s+/g, ' ').slice(0, 80)
    return { ok: false, message: `桥接接口返回非 JSON 响应（HTTP ${response.status}：${preview}）` }
  }
  if (isBridgeResultPayload(payload)) return payload as BridgeResult<T>
  return { ok: false, message: `invalid settings bridge payload (HTTP ${response.status})` }
}

/**
 * 当前**编辑目标**（模块 id）。
 *
 * 同一身份路径：模块内的配置卡启停、编辑与模块级写盘都按它定位——模块 id 唯一
 * （复制必带 `-copy` 后缀），加上卡自身的 id 就唯一确定一张配置卡。
 * 未设置时不带该头，host 侧维持既有语义（当前激活模块目录）。
 */
let editTarget: string | undefined

/** 由工作台在编辑目标变化时调用（来源：当前激活的模块）。 */
export function setEditTarget(moduleId: string | undefined): void {
  editTarget = moduleId !== undefined && moduleId.length > 0 ? moduleId : undefined
}

function editTargetHeader(): Record<string, string> {
  return editTarget === undefined ? {} : { [EDIT_TARGET_HEADER]: editTarget }
}

export async function postBridge<T>(path: string, body: unknown): Promise<BridgeResult<T>> {
  try {
    const response = await fetch(SETTINGS_BRIDGE_PREFIX + path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...editTargetHeader() },
      body: JSON.stringify(body ?? {}),
    })
    return await readBridgeResponse<T>(response)
  } catch (error) {
    return { ok: false, message: errorMessage(error) }
  }
}

/** 原始文件流上传；用于避开 JSON/base64 的额外膨胀。 */
export async function uploadBridge<T>(path: string, file: Blob, fileName: string): Promise<BridgeResult<T>> {
  try {
    const response = await fetch(SETTINGS_BRIDGE_PREFIX + path, {
      method: 'POST',
      headers: {
        'content-type': file.type || 'application/octet-stream',
        'x-file-name': encodeURIComponent(fileName),
        ...editTargetHeader(),
      },
      body: file,
    })
    return await readBridgeResponse<T>(response)
  } catch (error) {
    return { ok: false, message: errorMessage(error) }
  }
}

/** JSON 包装字段和少量路径元数据预留 4KiB，避免临界文件刚好超限。 */
export function shouldStreamJsonFile(file: Blob): boolean {
  return file.size >= MAX_BRIDGE_BODY_BYTES - 4 * 1024
}
